
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useIntegrations } from '@/hooks/integrations';
import { supabase } from '@/integrations/supabase/client';
import { Integration } from '@/types/integrations';

export const useNuvemshopConnection = (integration?: Integration) => {
  const { deleteIntegration, refetch } = useIntegrations();
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const handleConnectNuvemshop = async () => {
    try {
      console.log('🔗 [NUVEMSHOP] Iniciando redirecionamento OAuth');
      setIsConnecting(true);
      setConnectionError(null);

      // Obter user ID atual
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      console.log('📤 [NUVEMSHOP] Enviando requisição OAuth para Edge Function');

      // Iniciar fluxo OAuth
      const { data, error } = await supabase.functions.invoke('nuvemshop-integration', {
        body: { 
          action: 'start_oauth', 
          userId: user.id 
        }
      });

      if (error) {
        console.error('❌ [NUVEMSHOP] Erro da Edge Function:', error);
        throw new Error(error.message || 'Erro na comunicação com o servidor');
      }

      if (!data || !data.success) {
        console.error('❌ [NUVEMSHOP] Falha no OAuth:', data?.error);
        throw new Error(data?.error || 'Falha ao iniciar OAuth');
      }

      if (!data.auth_url) {
        console.error('❌ [NUVEMSHOP] URL de autorização não retornada');
        throw new Error('URL de autorização não foi gerada pelo servidor');
      }

      console.log('🌐 [NUVEMSHOP] Redirecionando para:', data.auth_url);
      
      // Redirecionar diretamente na mesma aba
      window.location.href = data.auth_url;
      
    } catch (error: any) {
      console.error('💥 [DEBUG] ERRO CAPTURADO NO CATCH:', error);
      
      let errorMessage = 'Erro desconhecido ao conectar com Nuvemshop';
      
      if (error.message?.includes('Popup foi bloqueado')) {
        errorMessage = 'Pop-ups estão bloqueados. Por favor, permita pop-ups para este site e tente novamente.';
      } else if (error.message?.includes('Failed to send a request')) {
        errorMessage = 'Falha na comunicação com o servidor. Verifique sua conexão.';
      } else if (error.message?.includes('JSON')) {
        errorMessage = 'Erro no formato da resposta do servidor.';
      } else if (error.message?.includes('Credenciais da Nuvemshop')) {
        errorMessage = 'Credenciais da Nuvemshop não configuradas. Entre em contato com o administrador.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao conectar",
        description: errorMessage,
        variant: "destructive"
      });
      
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!integration) return;
    
    if (confirm('Tem certeza que deseja desconectar a Nuvemshop?')) {
      setIsDisconnecting(true);
      
      try {
        await deleteIntegration(integration.id);
        
        // Aguardar 1 segundo e atualizar a interface
        setTimeout(() => {
          refetch();
          setIsDisconnecting(false);
        }, 1000);
        
      } catch (error) {
        setIsDisconnecting(false);
        // O erro já é tratado no deleteIntegration
      }
    }
  };

  return {
    handleConnectNuvemshop,
    handleDisconnect,
    isConnecting,
    isDisconnecting,
    connectionError
  };
};
